/**
 * Alpine component: lessonPlayer
 *
 * Gestisce l'overlay per la fruizione delle lezioni iSpring:
 * - Apertura/chiusura pannello slide-up con iframe
 * - Ascolto postMessage da logsession.js per progress real-time
 * - Aggiornamento progress bar inline alla chiusura
 * - Toggle fullscreen
 */
export function registerLessonPlayer(Alpine) {
    Alpine.data('lessonPlayer', (config) => ({
        // Config passata dal template PHP
        subId: config.subId || 0,
        ajaxBase: config.ajaxBase || '',
        lessons: config.lessons || [],

        // Stato overlay
        open: false,
        fullscreen: false,
        currentIndex: -1,
        lessonSrc: '',
        _scrollY: 0,

        // Progress reattivo per ogni lezione (copia locale)
        progress: [],

        init() {
            // Inizializza progress da dati SSR
            this.progress = this.lessons.map(l => ({
                id: l.id,
                percentage: l.percentage || 0,
            }));

            // Ascolta postMessage da logsession.js nell'iframe
            this._messageHandler = (e) => this.handleMessage(e);
            window.addEventListener('message', this._messageHandler);
        },

        destroy() {
            window.removeEventListener('message', this._messageHandler);
        },

        /**
         * Apre l'overlay con la lezione indicata.
         */
        openLesson(index) {
            const lesson = this.lessons[index];
            if (!lesson) return;

            // annulla le pendenze della chiusura precedente: il loro compito era
            // svuotare l'iframe, che ora serve alla lezione che stiamo aprendo
            if (this._cancelPendingClose) this._cancelPendingClose();

            this.currentIndex = index;
            this.lessonSrc = this.ajaxBase + '/lesson-player.php?sub=' + this.subId + '&lesson=' + lesson.id;
            this.open = true;
            this._scrollY = window.scrollY;
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.paddingRight = scrollBarWidth + 'px';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this._scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        },

        /**
         * Chiude l'overlay e aggiorna i dati dal server.
         *
         * Il log finale della lezione è una POST asincrona fatta da logsession.js
         * dentro l'iframe: finché non è confermata, il server non ha ancora il
         * progresso aggiornato. Rileggendo troppo presto, refreshTraining
         * sovrascriveva la progress bar con dati vecchi e la barra tornava
         * indietro (in locale non si vedeva: la POST rientrava nei 150ms).
         * Perciò l'overlay si chiude subito, ma il refresh attende la conferma
         * 'lesson:log:final:done' (con fallback se non arriva).
         */
        closeLesson() {
            const iframe = this.$root.querySelector('.ik-lesson-overlay__frame');

            let refreshed = false;
            let fallbackTimer = null;

            const cleanup = () => {
                refreshed = true;
                window.removeEventListener('message', onFinalDone);
                if (fallbackTimer) clearTimeout(fallbackTimer);
                this._cancelPendingClose = null;
            };

            const finishRefresh = () => {
                if (refreshed) return;
                cleanup();
                // Svuota l'iframe (il suo JS serviva per la conferma) solo se
                // l'overlay è ancora chiuso: se nel frattempo l'utente ha aperto
                // un'altra lezione, azzerarlo la lascerebbe con la finestra vuota.
                if (!this.open) this.lessonSrc = '';
                this.refreshTraining();
            };

            const onFinalDone = (e) => {
                if (e.data && e.data.message === 'lesson:log:final:done') finishRefresh();
            };
            window.addEventListener('message', onFinalDone);

            // Se l'utente riapre una lezione prima che la chiusura si concluda,
            // openLesson annulla queste pendenze (niente iframe svuotato a sorpresa).
            this._cancelPendingClose = cleanup;

            // Invia messaggio all'iframe per triggerare log finale
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage({ message: 'lesson:close' }, '*');
            }

            // Fallback: rete lenta, errore o player che non conferma
            fallbackTimer = setTimeout(finishRefresh, 3000);

            // Piccolo delay per permettere al fetch keepalive di partire
            setTimeout(() => {
                this.open = false;
                this.fullscreen = false;
                this.currentIndex = -1;
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
                window.scrollTo(0, this._scrollY);
            }, 150);
        },

        /**
         * Toggle fullscreen overlay.
         */
        toggleFullscreen() {
            this.fullscreen = !this.fullscreen;
        },

        /**
         * Titolo lezione corrente.
         */
        get currentTitle() {
            if (this.currentIndex < 0) return '';
            return this.lessons[this.currentIndex]?.title || '';
        },

        /**
         * Gestisce i messaggi postMessage da logsession.js.
         * Messaggi attesi: 'lesson:log', 'lesson:log:success', 'lesson:log:error'
         */
        handleMessage(event) {
            const msg = event.data;
            if (!msg || !msg.message) return;

            if (msg.message === 'lesson:log' || msg.message === 'lesson:log:success') {
                this.updateProgressFromMessage(msg.data);
            }
        },

        /**
         * Aggiorna la progress bar della lezione corrente dai dati postMessage.
         */
        updateProgressFromMessage(data) {
            if (this.currentIndex < 0 || !data?.lessonData) return;

            const lesson = this.lessons[this.currentIndex];
            if (!lesson || !lesson.slideCount) return;

            const viewed = data.lessonData.viewed_counter || 0;
            const total = lesson.slideCount;
            const pct = Math.min(100, Math.round((viewed / total) * 100));

            this.progress[this.currentIndex].percentage = pct;
        },

        /**
         * Richiama il proxy training per aggiornare i dati definitivi.
         */
        async refreshTraining() {
            try {
                const res = await fetch(
                    this.ajaxBase + '/training.php?sub=' + this.subId
                );
                if (!res.ok) return;

                const training = await res.json();
                const serverLessons = training.lessons || [];
                const counter = training.counter?.lessons || {};

                // Aggiorna progress lezioni e calcola media percentuali
                let sum = 0;
                serverLessons.forEach((sl) => {
                    const idx = this.lessons.findIndex(l => l.id === sl.id);
                    if (idx !== -1) {
                        this.progress[idx].percentage = sl.percentage || 0;
                    }
                    sum += (sl.percentage || 0);
                });
                const avgPct = serverLessons.length > 0
                    ? Math.round(sum / serverLessons.length)
                    : 0;

                // Notifica il parent per aggiornare lo stepper
                this.$dispatch('training-updated', {
                    lsPct: avgPct,
                    lsDone: counter.completed ?? 0,
                    lsTotal: counter.total ?? 0,
                });

                // Se il modulo formativo è al 100%, notifica completamento
                if (avgPct >= 100) {
                    this.$dispatch('lesson-module-completed');
                }
            } catch (e) {
                // Silenzioso — i dati SSR restano validi
            }
        },
    }));
}
